/**
 * PieceCapture Mini-Game
 *
 * Self-contained game: Engine + Definition + Registration
 *
 * Rules:
 * - Player has one or more pieces (e.g., a Knight)
 * - Opponent has some pieces (e.g., pawns) as static targets
 * - Player must capture all opponent pieces
 * - Opponent does NOT make any moves
 * - No kings required (not a legal chess position)
 * - Win: All opponent pieces captured
 * - Fail: Move limit exceeded (if set)
 */
import { MiniGameBase } from "../MiniGameBase";
import { MiniGameRegistry } from "../MiniGameRegistry";
import * as BoardUtils from "../BoardUtils";
// ========================================
// Helper Functions
// ========================================
/**
 * Extract the active color from a FEN string.
 * The active color is the second space-separated field.
 */
function getActiveColorFromFen(fen) {
    const parts = fen.split(" ");
    return parts[1] === "b" ? "b" : "w";
}
// ========================================
// Engine
// ========================================
export class PieceCaptureEngine extends MiniGameBase {
    constructor(config) {
        super(config);
    }
    /**
     * Get the opponent color.
     */
    getOpponentColor() {
        return this.config.playerColor === "w" ? "b" : "w";
    }
    /**
     * Initialize game state with target pieces (opponent pieces to capture).
     */
    initializeState() {
        // Parse FEN to get initial pieces
        const board = BoardUtils.parseFenToBoard(this.config.fen);
        if (board) {
            this.pieces = board.pieces;
        }
        // Identify target pieces (all opponent pieces)
        const opponentColor = this.getOpponentColor();
        const targetPieces = this.pieces.filter((p) => p.color === opponentColor);
        const targetSquares = targetPieces.map((p) => p.square);
        return {
            fen: this.config.fen,
            moves: [],
            moveCount: 0,
            targetsAchieved: 0,
            totalTargets: targetPieces.length,
            remainingTargets: [...targetSquares],
            foundSquares: [],
            wrongSquares: [],
            status: "playing",
            capturedPieces: [],
            initialTargetPieces: [...targetPieces],
        };
    }
    /**
     * Get valid moves for a piece on a square.
     * Only pieces of the player's color can move.
     */
    getValidMoves(square) {
        const piece = BoardUtils.getPieceAt(this.pieces, square);
        if (!piece)
            return [];
        // Only player's pieces can move
        if (piece.color !== this.config.playerColor)
            return [];
        // Get valid move squares (attacks minus own pieces, plus pawn pushes if applicable)
        return BoardUtils.getValidMoveSquares(square, piece.type, piece.color, this.pieces, { includePawnPushes: piece.type === "p" });
    }
    /**
     * Make a move.
     */
    makeMove(from, to) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        // Validate the move
        const validMoves = this.getValidMoves(from);
        if (!validMoves.includes(to)) {
            return { valid: false, error: "Invalid move" };
        }
        // Execute the move
        const moveResult = BoardUtils.makeMove(this.pieces, from, to);
        this.pieces = moveResult.pieces;
        const captured = moveResult.captured;
        // Update move count
        this.state.moveCount++;
        this.state.moves.push({
            from,
            to,
            captured: captured?.type,
        });
        // Update FEN
        this.updateFen();
        // Track if a target was captured (any opponent piece)
        const opponentColor = this.getOpponentColor();
        let targetHit = false;
        if (captured && captured.color === opponentColor) {
            targetHit = true;
            this.state.capturedPieces = [...this.state.capturedPieces, captured];
            this.state.targetsAchieved++;
            this.state.remainingTargets = this.state.remainingTargets.filter((t) => t !== to);
            this.state.foundSquares = [...this.state.foundSquares, to];
        }
        // Check win condition: all opponent pieces captured
        if (this.state.remainingTargets.length === 0) {
            this.state.status = "complete";
            return {
                valid: true,
                newFen: this.state.fen,
                targetHit,
                targetSquare: targetHit ? to : undefined,
                gameComplete: true,
            };
        }
        // Check fail condition: move limit exceeded
        if (this.isMoveLimitExceeded()) {
            this.state.status = "failed";
            return {
                valid: true,
                newFen: this.state.fen,
                targetHit,
                gameFailed: true,
                error: "Move limit exceeded!",
            };
        }
        return {
            valid: true,
            newFen: this.state.fen,
            targetHit,
            targetSquare: targetHit ? to : undefined,
        };
    }
    /**
     * Get a hint - suggest a move that captures a target piece.
     */
    getHint() {
        if (this.state.status !== "playing")
            return null;
        const playerColor = this.config.playerColor;
        const opponentColor = this.getOpponentColor();
        // Find player pieces that can capture opponent pieces
        const playerPieces = this.pieces.filter((p) => p.color === playerColor);
        const targetPieces = this.pieces.filter((p) => p.color === opponentColor);
        const targetSquares = new Set(targetPieces.map((p) => p.square));
        // Find a capturing move
        for (const piece of playerPieces) {
            const validMoves = this.getValidMoves(piece.square);
            for (const move of validMoves) {
                if (targetSquares.has(move)) {
                    return { from: piece.square, to: move };
                }
            }
        }
        // No immediate capture available - just suggest any valid move
        for (const piece of playerPieces) {
            const validMoves = this.getValidMoves(piece.square);
            if (validMoves.length > 0) {
                return { from: piece.square, to: validMoves[0] };
            }
        }
        return null;
    }
    /**
     * Get extended state including PieceCapture-specific fields.
     */
    getState() {
        return { ...this.state };
    }
    reset() {
        this.state = this.initializeState();
    }
}
// ========================================
// Registration
// ========================================
MiniGameRegistry.register({
    type: "PIECE_CAPTURE",
    createEngine: (config) => new PieceCaptureEngine(config),
    validate: (config) => {
        const errors = [];
        const playerColor = getActiveColorFromFen(config.fen);
        const opponentColor = playerColor === "w" ? "b" : "w";
        // Validate FEN has pieces
        const board = BoardUtils.parseFenToBoard(config.fen);
        if (!board || board.pieces.length === 0) {
            errors.push("FEN must contain pieces");
        }
        // Check for at least one player piece (attacker)
        const playerPieces = board?.pieces.filter((p) => p.color === playerColor) || [];
        if (playerPieces.length === 0) {
            errors.push(`FEN must contain at least one ${playerColor === "w" ? "white" : "black"} piece (attacker)`);
        }
        // Check for at least one opponent piece (target)
        const opponentPieces = board?.pieces.filter((p) => p.color === opponentColor) || [];
        if (opponentPieces.length === 0) {
            errors.push(`FEN must contain at least one ${opponentColor === "w" ? "white" : "black"} piece (target)`);
        }
        return { valid: errors.length === 0, errors };
    },
    getInstructionText: (config) => {
        const playerColor = getActiveColorFromFen(config.fen);
        const opponentName = playerColor === "w" ? "black" : "white";
        // Build instruction
        let instruction = `Capture all the ${opponentName} pieces`;
        // Add move limit info if set
        if (config.moveLimit) {
            instruction += ` in ${config.moveLimit} move${config.moveLimit === 1 ? "" : "s"} or less`;
        }
        return instruction + ".";
    },
    supportsHints: true,
    interactionType: "drag",
    /**
     * Extract target squares from FEN (opponent pieces).
     * Player color is derived from FEN, so the parameter is unused.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    extractTargetsFromFen: (fen, _playerColor) => {
        const board = BoardUtils.parseFenToBoard(fen);
        if (!board)
            return [];
        const activeColor = getActiveColorFromFen(fen);
        const opponentColor = activeColor === "w" ? "b" : "w";
        return board.pieces
            .filter((p) => p.color === opponentColor)
            .map((p) => p.square);
    },
});
